import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Chip,
  Link,
  LinearProgress,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import HistoryIcon from '@mui/icons-material/History';
import {
  groupDocumentsByType,
  groupDocumentsByDiscipline,
  DOCUMENT_TYPE_OPTIONS,
} from './document-type-groups';

// Whether a drag carries files (not, say, selected text).
export const isFileDrag = (event) =>
  Array.from(event.dataTransfer?.types || []).includes('Files');

const STATUS_STYLES = {
  ready: { label: 'Ready', bgcolor: '#e3f3e6', color: '#1e6b2e' },
  processing: { label: 'Processing', bgcolor: '#fdecc8', color: '#7a4e04' },
  review_required: {
    label: 'Review Required',
    bgcolor: '#fdecc8',
    color: '#7a4e04',
  },
  error: { label: 'Error', bgcolor: '#fcebeb', color: '#a32d2d' },
  // Projected by documents-db.js's attachProcessingStatus from the durable
  // project_document_review_decisions row -- the underlying
  // project_document_processing_status.status column stays 'review_required'
  // forever (rejection is terminal and never mutates it); this is a
  // read-time-only relabeling, never a second status written to the DB.
  review_rejected: {
    label: 'Review Rejected',
    bgcolor: '#fcebeb',
    color: '#a32d2d',
  },
};

/**
 * RAG internal stage enum -> friendly display text. Never expose a raw
 * enum value to the user -- an unrecognized/missing stage falls back to
 * the generic 'Processing' label below, never the raw string.
 *
 * Each durable stage value is a COMPLETION/CHECKPOINT marker in the RAG
 * pipeline's own state machine, not a "work currently happening" marker --
 * e.g. the CHUNKING event is emitted AFTER extraction+scan+chunking have
 * already completed and immediately BEFORE the (expensive) embedding call
 * begins, and EMBEDDING is emitted only after embedding has finished. This
 * mapping is therefore deliberately offset by one step from a literal
 * enum-name translation: it describes the work actually occurring AFTER
 * each durable milestone was persisted, not the milestone's own name. The
 * durable stage timing itself is intentionally NEVER changed for this --
 * see app.fieldtime_processing_orchestrator's own milestone placement,
 * which participates in resume/checkpoint behavior this UI must never
 * influence. This mapping is presentation-only.
 */
const STAGE_LABELS = {
  security_scan: 'Preparing document',
  parsing: 'Extracting document',
  sensitive_content_scan: 'Content review',
  chunking: 'Generating embeddings',
  embedding: 'Preparing search index',
  indexing: 'Indexing',
  verification: 'Verifying',
};

// Deliberately distinct text from STATUS_STYLES.processing.label ('Processing')
// -- both can appear in the same document row (the existing status Chip
// plus this fallback stage line), and must never collide as an exact-text
// match for anything querying by that label.
const FALLBACK_STAGE_LABEL = 'Processing...';

/**
 * Clamp a possibly-malformed progress_percent to 0..100 for RENDERING only
 * -- never mutates the backend value and never invents a percentage the
 * backend didn't report (a missing/non-numeric value renders with no
 * percent text and a 0% bar, never a fabricated number).
 */
const clampedProgressPercent = (value) => {
  if (typeof value !== 'number' || !Number.isFinite(value)) return null;
  return Math.min(100, Math.max(0, value));
};

/**
 * Compact determinate progress treatment for a document currently being
 * processed -- stage label + percentage as TEXT (never color alone) plus a
 * determinate MUI LinearProgress, which sets role="progressbar"/
 * aria-valuenow/aria-valuemin/aria-valuemax automatically from `value`.
 * Renders nothing for any status other than "processing" (ready, error, or
 * a future terminal status such as review_required) -- an active progress
 * indicator must never appear once a document has reached a terminal
 * outcome, and must never animate at 100%/Ready.
 */
const ProcessingProgressDetail = ({ processingStatus }) => {
  if (processingStatus?.status !== 'processing') return null;

  const stageLabel =
    STAGE_LABELS[processingStatus.stage] || FALLBACK_STAGE_LABEL;
  const clampedPercent = clampedProgressPercent(
    processingStatus.progress_percent
  );

  return (
    <Box sx={{ mt: 0.25 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.25 }}>
        <Typography
          variant="caption"
          sx={{ fontSize: 9, color: 'text.secondary' }}
        >
          {stageLabel}
        </Typography>
        {clampedPercent !== null && (
          <Typography
            variant="caption"
            sx={{ fontSize: 9, color: 'text.secondary' }}
          >
            {clampedPercent}%
          </Typography>
        )}
      </Box>
      <LinearProgress
        variant="determinate"
        value={clampedPercent ?? 0}
        sx={{ height: 4, borderRadius: 2 }}
      />
    </Box>
  );
};

/**
 * Small pill matching the attached UI's Ready/Processing/Error treatment.
 * Renders nothing for a document with no processing_status record (e.g. a
 * legacy document that predates this projection) -- never fabricates a
 * status the backend has no evidence for.
 */
const ProcessingStatusBadge = ({ processingStatus }) => {
  const style = STATUS_STYLES[processingStatus?.status];
  if (!style) return null;
  const title =
    processingStatus.status === 'error' && processingStatus.error_message
      ? processingStatus.error_message
      : processingStatus.status === 'processing' &&
          Number.isInteger(processingStatus.progress_percent)
        ? `${processingStatus.progress_percent}% complete`
        : undefined;
  return (
    <Chip
      size="small"
      label={style.label}
      title={title}
      sx={{
        height: 18,
        fontSize: 9,
        fontWeight: 600,
        bgcolor: style.bgcolor,
        color: style.color,
        '& .MuiChip-label': { px: 1 },
      }}
    />
  );
};

/**
 * Accessible "Review" action for a document whose current status is
 * review_required -- renders nothing for any other status. onReviewClick
 * receives the document itself; the caller (ProjectDocumentsAssistantPage)
 * owns loading the actual review details and the Approve/Reject dialog.
 */
const ReviewAction = ({ processingStatus, onClick }) => {
  if (processingStatus?.status !== 'review_required') return null;
  return (
    <Link
      component="button"
      type="button"
      variant="caption"
      underline="always"
      onClick={onClick}
      sx={{ fontSize: 9, fontWeight: 600, whiteSpace: 'nowrap' }}
    >
      Review
    </Link>
  );
};

/**
 * "Retry processing" action for a document whose current status is
 * "error" -- renders nothing for any other status (ready/processing/
 * review_required/review_rejected), mirroring ReviewAction's own
 * single-status gate exactly. onClick receives the document itself; the
 * caller (ProjectDocumentsAssistantPage) owns the actual retry API call
 * and the subsequent authoritative refetch. Disabled while a retry request
 * for THIS document is already in flight, so a double click can never
 * submit two retry requests.
 */
const RetryAction = ({ processingStatus, retrying, onClick }) => {
  if (processingStatus?.status !== 'error') return null;
  return (
    <Link
      component="button"
      type="button"
      variant="caption"
      underline="always"
      disabled={retrying}
      onClick={onClick}
      sx={{
        fontSize: 9,
        fontWeight: 600,
        whiteSpace: 'nowrap',
        opacity: retrying ? 0.6 : 1,
      }}
    >
      {retrying ? 'Retrying…' : 'Retry processing'}
    </Link>
  );
};

/**
 * Opens a document's Version history (DocumentVersionHistoryDialog, owned
 * by the caller). The same button-Link as the actions above, but an icon
 * rather than text so every row stays compact; the accessible name carries
 * the document's title.
 */
const VersionsAction = ({ documentName, onClick }) => (
  <Link
    component="button"
    type="button"
    aria-label={`Version history for ${documentName}`}
    title="Version history"
    onClick={onClick}
    sx={{ display: 'inline-flex', color: 'text.secondary' }}
  >
    <HistoryIcon sx={{ fontSize: 14 }} />
  </Link>
);

/**
 * Collapsible group section shared by both nesting levels the
 * DocumentsPane renders: the outer type-level groups (Drawings/Specs/
 * RFIs/Other documents, from groupDocumentsByType) and, nested one level
 * inside each of those, the discipline-level subgroups (from
 * groupDocumentsByDiscipline). Both levels get the identical caret/count-
 * badge/expand-collapse treatment because this is the SAME component
 * either way -- not a second, parallel implementation.
 *
 * `children`, when supplied (non-null), is rendered as this group's body
 * INSTEAD of a flat list of document rows -- this is how the outer,
 * type-level group renders nested discipline DocumentGroups rather than
 * documents directly. A type group with no documents at all (e.g. an
 * empty RFIs section) has nothing to nest, so it still falls back to the
 * plain leaf-row rendering below (which then shows "No documents."); a
 * discipline-level DocumentGroup never receives `children` and always
 * renders its own documents as leaf rows.
 *
 * Auto-expand-on-first-population (the effect below) is intentionally
 * left exactly as-is for both levels rather than special-cased per level,
 * and does not double-fire: a discipline subgroup is only ever mounted
 * once it already has at least one document (DocumentsPane never renders
 * an empty discipline subgroup -- see groupDocumentsByDiscipline, which
 * never returns an empty group), so a subgroup component always starts
 * already-expanded via its own initial `useState` above, never via this
 * transition effect. The effect's 0-to-N transition therefore only ever
 * has real work to do at the outer, type level (e.g. Drawings receiving
 * its first-ever document while already mounted and collapsed) -- exactly
 * the one case it already handled before nesting existed.
 */
const DocumentGroup = ({
  name,
  documents,
  onReviewClick,
  onRetryClick,
  onVersionsClick,
  retryingDocumentId,
  onFilesDropped = null,
  dropTypeLabel = null,
  children = null,
}) => {
  const [expanded, setExpanded] = useState(documents.length > 0);
  const [dragOver, setDragOver] = useState(false);
  const previousCountRef = useRef(documents.length);

  // Plan section 6: a section is a drop target for files (only the outer,
  // type-level sections get onFilesDropped). Dropped files go to the same
  // Upload dialog as "+ Upload", preset to this section's type.
  const dropTargetProps = onFilesDropped
    ? {
        onDragOver: (e) => {
          if (!isFileDrag(e)) return;
          e.preventDefault();
          setDragOver(true);
        },
        onDragLeave: (e) => {
          if (!e.currentTarget.contains(e.relatedTarget)) setDragOver(false);
        },
        onDrop: (e) => {
          if (!isFileDrag(e)) return;
          e.preventDefault();
          setDragOver(false);
          onFilesDropped(e.dataTransfer.files);
        },
      }
    : {};

  // A group that starts empty (collapsed by default) must not stay
  // collapsed forever once a document actually lands in it -- e.g. a
  // freshly-uploaded document must be visible immediately, not hidden
  // behind a group the user never had a reason to expand yet.
  useEffect(() => {
    if (previousCountRef.current === 0 && documents.length > 0) {
      setExpanded(true);
    }
    previousCountRef.current = documents.length;
  }, [documents.length]);

  return (
    <Box
      data-testid={onFilesDropped ? `document-drop-target-${name}` : undefined}
      {...dropTargetProps}
      sx={{
        mb: 0.5,
        borderRadius: 1,
        outline: dragOver ? '2px dashed' : 'none',
        outlineColor: 'primary.main',
        bgcolor: dragOver ? 'action.hover' : 'transparent',
      }}
    >
      {dragOver && (
        <Typography
          variant="caption"
          color="primary"
          sx={{ display: 'block', px: 0.5, fontWeight: 600 }}
        >
          Drop to add as {dropTypeLabel}
        </Typography>
      )}
      <Box
        role="button"
        tabIndex={0}
        onClick={() => setExpanded((prev) => !prev)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setExpanded((prev) => !prev);
          }
        }}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          py: 0.5,
          cursor: 'pointer',
          userSelect: 'none',
        }}
      >
        {expanded ? (
          <ExpandMoreIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
        ) : (
          <ChevronRightIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
        )}
        <Typography
          variant="caption"
          sx={{ fontWeight: 600, color: 'text.primary' }}
        >
          {name}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          ({documents.length})
        </Typography>
      </Box>
      {expanded && (
        <Box sx={{ pl: 2.5 }}>
          {children ? (
            children
          ) : documents.length === 0 ? (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', py: 0.5 }}
            >
              No documents.
            </Typography>
          ) : (
            documents.map((document) => (
              <Box key={document.id} sx={{ py: 0.5 }}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'text.primary',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                    title={document.title || document.file_name}
                  >
                    {document.title || document.file_name}
                  </Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.75,
                      flexShrink: 0,
                    }}
                  >
                    <ReviewAction
                      processingStatus={document.processing_status}
                      onClick={() => onReviewClick(document)}
                    />
                    <RetryAction
                      processingStatus={document.processing_status}
                      retrying={retryingDocumentId === document.id}
                      onClick={() => onRetryClick(document)}
                    />
                    <ProcessingStatusBadge
                      processingStatus={document.processing_status}
                    />
                    {onVersionsClick && (
                      <VersionsAction
                        documentName={document.title || document.file_name}
                        onClick={() => onVersionsClick(document)}
                      />
                    )}
                  </Box>
                </Box>
                <ProcessingProgressDetail
                  processingStatus={document.processing_status}
                />
              </Box>
            ))
          )}
        </Box>
      )}
    </Box>
  );
};

// The pane's width until the user resizes it (see ColumnResizeHandle).
export const DOCUMENTS_PANE_DEFAULT_WIDTH = 280;

/**
 * Right-hand documents pane: real Project Documents records for the
 * current project + discipline scope, grouped for display and showing
 * each current version's real processing status (never a fake/hard-coded
 * status).
 */
const DocumentsPane = ({
  documents,
  loading,
  error,
  onUploadClick,
  onFilesDropped,
  canUpload,
  onReviewClick,
  onRetryClick,
  onVersionsClick,
  retryingDocumentId,
  width = DOCUMENTS_PANE_DEFAULT_WIDTH,
}) => {
  const groups = groupDocumentsByType(documents);

  return (
    <Box
      data-testid="documents-pane"
      sx={{
        width,
        flexShrink: 0,
        borderLeft: 1,
        borderColor: 'divider',
        bgcolor: '#fafbfc',
        p: 1.75,
        overflowY: 'auto',
        minWidth: 0,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 1.5,
        }}
      >
        <Typography
          variant="caption"
          sx={{ color: 'text.secondary', letterSpacing: 0.3, fontWeight: 600 }}
        >
          DOCUMENTS
        </Typography>
        {/* One Upload action: the multi-file workflow handles one file or many. */}
        {canUpload && (
          <Link
            component="button"
            variant="caption"
            underline="none"
            onClick={onUploadClick}
            sx={{ fontWeight: 600 }}
          >
            + Upload
          </Link>
        )}
      </Box>

      {/* Project Assistant Schedule Tools -- FINAL PRODUCT DIRECTION: the
          entire schedule feature is a CHAT capability ("make the piping
          schedule", "download it", "upload it to RAG", ...) with the
          resulting downloadable artifact surfaced directly at the bottom of
          the assistant's own chat message (see ChatPanel.js's
          ToolResultCard) -- never a link/button/dialog here. DocumentsPane
          stays about project documents only; it has no schedule-related
          affordance of any kind. */}

      {error && (
        <Typography
          variant="caption"
          color="error"
          sx={{ display: 'block', mb: 1 }}
        >
          {error}
        </Typography>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress size={20} />
        </Box>
      ) : (
        groups.map((group) => {
          // Second nesting level: within THIS type group, further split by
          // discipline_name. Only ever nonempty when group.documents is
          // nonempty (see groupDocumentsByDiscipline) -- an empty type
          // group (e.g. no RFIs yet) has no subgroups to nest, so its
          // DocumentGroup falls back to its own plain "No documents." body.
          const disciplineGroups = groupDocumentsByDiscipline(group.documents);
          return (
            <DocumentGroup
              key={group.name}
              name={group.name}
              documents={group.documents}
              dropTypeLabel={
                DOCUMENT_TYPE_OPTIONS.find((o) => o.id === group.documentTypeId)
                  .label
              }
              onFilesDropped={
                canUpload && onFilesDropped
                  ? (files) => onFilesDropped(group.documentTypeId, files)
                  : null
              }
            >
              {disciplineGroups.length > 0
                ? disciplineGroups.map((subgroup) => (
                    <DocumentGroup
                      key={subgroup.name}
                      name={subgroup.name}
                      documents={subgroup.documents}
                      onReviewClick={onReviewClick}
                      onRetryClick={onRetryClick}
                      onVersionsClick={onVersionsClick}
                      retryingDocumentId={retryingDocumentId}
                    />
                  ))
                : null}
            </DocumentGroup>
          );
        })
      )}
    </Box>
  );
};

export default DocumentsPane;
